import { useCallback, useEffect, useRef, useState } from 'react'
import { loadProspectingDataset } from '../data/prospectingSheets'
import { pendingLeadsReconciliation, syncProspectingLeads } from '../features/prospecting/prospectingSyncClient'
import type { ProspectingDataset } from '../types/prospecting'

export type ProspectingDataState = {
  data: ProspectingDataset | null
  status: 'loading' | 'ready' | 'error'
  error: string | null
  refreshing: boolean
  refresh: () => void
  /**
   * Turns on the background Hot/Audit/Old Leads -> Prospecting Leads
   * reconciliation. The Prospecting page calls this, so lifting the dataset to
   * the application shell does not start writing on every page.
   */
  enableReconciliation: () => void
}

export function useProspectingData(): ProspectingDataState {
  const [data, setData] = useState<ProspectingDataset | null>(null)
  const [status, setStatus] = useState<ProspectingDataState['status']>('loading')
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [requestKey, setRequestKey] = useState(0)
  const [reconciliationEnabled, setReconciliationEnabled] = useState(false)
  // Every set of missing prospects is only ever synced once per session. If the
  // published CSV export is still serving a pre-append snapshot, the same
  // signature comes back and is skipped instead of looping.
  const syncedSignatures = useRef(new Set<string>())

  const refresh = useCallback(() => {
    setRequestKey((current) => current + 1)
  }, [])

  const enableReconciliation = useCallback(() => {
    setReconciliationEnabled(true)
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    if (requestKey > 0) setRefreshing(true)
    else setStatus('loading')

    setError(null)

    loadProspectingDataset(controller.signal, { cacheBust: requestKey > 0 })
      .then((nextData) => {
        if (controller.signal.aborted) return
        setData(nextData)
        setStatus('ready')
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause instanceof Error ? cause.message : 'Prospecting data could not be loaded.')
        setStatus('error')
      })
      .finally(() => {
        if (!controller.signal.aborted) setRefreshing(false)
      })

    return () => controller.abort()
  }, [requestKey])

  // Leads is the master working list, so anything William adds to Hot, Audit
  // or Old Leads is reconciled into it in the background. A reconciliation
  // that wrote rows re-reads the workbook so the page shows the new rows.
  useEffect(() => {
    if (!reconciliationEnabled || !data) return

    const controller = new AbortController()
    const pending = pendingLeadsReconciliation(data)
    if (pending.count === 0 || syncedSignatures.current.has(pending.signature)) return
    syncedSignatures.current.add(pending.signature)

    syncProspectingLeads(controller.signal)
      .then((result) => {
        if (controller.signal.aborted || result.appended === 0) return
        setRequestKey((current) => current + 1)
      })
      .catch(() => {
        // Reconciliation is a background convenience. A disabled or unreachable
        // sync service must never break the prospecting page.
      })

    return () => controller.abort()
  }, [data, reconciliationEnabled])

  return { data, status, error, refreshing, refresh, enableReconciliation }
}
